import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { TutorAvatar } from './TutorAvatar.tsx';
import { Icon } from './Icon.tsx';

const tokensOf = (value: string) =>
  value.match(/\d+(?:\.\d+)?(?:\/\d+)?|[a-z]|[+−\-×*/=()]/gi) ?? [];
export function EquationWorkshop({
  value,
  onChange,
  onCheck,
  transfer = false,
  speaking = false,
  focusTerm = '',
  onSelect,
  disabled = false,
  feedback,
  correct = false,
  sketch,
  prices: suppliedPrices,
  onModeChange,
}: {
  value: string;
  onChange: (value: string) => void;
  onCheck: () => void;
  transfer?: boolean;
  speaking?: boolean;
  focusTerm?: string;
  onSelect?: (token: string) => void;
  disabled?: boolean;
  feedback?: string;
  correct?: boolean;
  sketch?: ReactNode;
  prices?: string[];
  onModeChange?: () => void;
}) {
  const [mode, setMode] = useState<'build' | 'sketch'>('build');
  const [insertAt, setInsertAt] = useState<number | null>(null);
  const board = useRef<HTMLDivElement>(null);
  const [target, setTarget] = useState({ x: 60, y: 28 });
  const tokens = tokensOf(value);
  const tutorX = Math.max(4, Math.min(65, target.x - 22));
  const prices =
    suppliedPrices ?? (transfer ? ['8', '4', '20'] : ['6', '3', '18']);
  const append = (token: string) => {
    if (disabled) return;
    const next = [...tokens];
    next.splice(insertAt ?? tokens.length, 0, token);
    onChange(next.join(' '));
    setInsertAt(null);
    onSelect?.(token);
  };
  useEffect(() => {
    const update = () => {
      const root = board.current;
      if (!root) return;
      const items = [
        ...root.querySelectorAll<HTMLElement>('[data-board-token]'),
        ...root
          .closest('section')!
          .querySelectorAll<HTMLElement>('.story-parts [data-board-token]'),
      ];
      const el =
        items.find((el) => el.dataset.boardToken === focusTerm) ||
        root.querySelector<HTMLElement>('.equation-drop');
      if (!el) return;
      const b = root.getBoundingClientRect(),
        r = el.getBoundingClientRect();
      setTarget({
        x: ((r.left + r.width / 2 - b.left) / b.width) * 100,
        y: ((r.top + r.height / 2 - b.top) / b.height) * 100,
      });
    };
    update();
    const observer = new ResizeObserver(update);
    if (board.current) observer.observe(board.current);
    return () => observer.disconnect();
  }, [value, focusTerm, mode]);
  const drop = (event: DragEvent, at = tokens.length) => {
    event.preventDefault();
    if (disabled) return;
    const payload = event.dataTransfer.getData('application/x-enactry-token');
    if (!payload) return;
    try {
      const data = JSON.parse(payload) as { token?: string; from?: number };
      if (!data.token || !/^([0-9]+|x|[+−\-×*/=()])$/.test(data.token)) return;
      const next = [...tokens];
      let dest = at;
      if (
        typeof data.from === 'number' &&
        data.from >= 0 &&
        data.from < next.length
      ) {
        next.splice(data.from, 1);
        if (data.from < dest) dest--;
      }
      next.splice(dest, 0, data.token);
      onChange(next.join(' '));
      onSelect?.(data.token);
    } catch {
      /* Only the workshop's own tokens are accepted. */
    }
  };
  return (
    <section className="equation-workshop" aria-label="Equation whiteboard">
      <div className="board-topline">
        <span>Your whiteboard</span>
        <div className="board-modes">
          <button
            aria-pressed={mode === 'build'}
            onClick={() => {
              onModeChange?.();
              setMode('build');
            }}
          >
            Build
          </button>
          <button
            aria-pressed={mode === 'sketch'}
            onClick={() => {
              onModeChange?.();
              setMode('sketch');
            }}
          >
            Sketch
          </button>
        </div>
      </div>
      <div className="story-parts" aria-label="Rental story parts">
        {prices.map((price, i) => (
          <button
            key={i}
            data-board-token={price}
            className={focusTerm === price ? 'focused' : ''}
            draggable={!disabled}
            onDragStart={(e) =>
              e.dataTransfer.setData(
                'application/x-enactry-token',
                JSON.stringify({ token: price }),
              )
            }
            onClick={() => append(price)}
            disabled={disabled}
            aria-label={`Add ${price}, ${['starting charge', 'hourly rate', 'total cost'][i]}`}
          >
            <strong>
              ${price}
              {i === 1 && <small>/ hour</small>}
            </strong>
            <span>
              {['starting charge', 'hourly rate', 'receipt total'][i]}
            </span>
          </button>
        ))}
      </div>
      <div className="equation-workspace" ref={board}>
        {mode === 'build' ? (
          <>
            <div
              className={`equation-drop ${tokens.length ? 'has-tokens' : ''}`}
              aria-label="Drop equation parts here"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => drop(e)}
            >
              {tokens.length ? (
                tokens.map((token, i) => (
                  <button
                    type="button"
                    key={`${i}-${token}`}
                    className={`equation-piece ${focusTerm === token ? 'focused' : ''} ${insertAt === i ? 'insertion' : ''}`}
                    data-board-token={token}
                    draggable={!disabled}
                    onDragStart={(e) => {
                      e.stopPropagation();
                      e.dataTransfer.setData(
                        'application/x-enactry-token',
                        JSON.stringify({ token, from: i }),
                      );
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.stopPropagation();
                      drop(e, i);
                    }}
                    onClick={() => {
                      setInsertAt(i);
                      onSelect?.(token);
                    }}
                    disabled={disabled}
                    aria-label={`Equation part ${i + 1}: ${token}. Select to insert before it.`}
                  >
                    {token}
                  </button>
                ))
              ) : (
                <div className="board-empty">
                  <span>Build your equation here</span>
                  <small>
                    Drag a story number, tap a symbol, type, or say your idea.
                  </small>
                </div>
              )}
            </div>
            <div className="symbol-palette" aria-label="Equation symbols">
              {['x', '+', '−', '×', '='].map((token) => (
                <button
                  key={token}
                  draggable={!disabled}
                  onDragStart={(e) =>
                    e.dataTransfer.setData(
                      'application/x-enactry-token',
                      JSON.stringify({ token }),
                    )
                  }
                  onClick={() => append(token)}
                  disabled={disabled}
                  aria-label={`Add ${token}`}
                >
                  {token}
                </button>
              ))}
              <button
                aria-label={
                  insertAt === null
                    ? 'Remove last equation part'
                    : `Remove equation part ${insertAt + 1}`
                }
                disabled={disabled || !tokens.length}
                onClick={() => {
                  const next = [...tokens];
                  next.splice(insertAt ?? next.length - 1, 1);
                  onChange(next.join(' '));
                  setInsertAt(null);
                }}
              >
                ⌫
              </button>
            </div>
            {insertAt !== null && (
              <button
                className="insertion-help"
                onClick={() => setInsertAt(null)}
              >
                Adding before part {insertAt + 1} · tap here to add at the end
              </button>
            )}
          </>
        ) : (
          sketch
        )}
        <svg
          className="builder-pointer"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <line
            x1={tutorX + 3}
            y1="83"
            x2={target.x}
            y2={Math.min(55, target.y)}
            stroke="#b29560"
            strokeWidth=".6"
          />
          <circle
            cx={target.x}
            cy={Math.min(55, target.y)}
            r="1"
            fill="#b07b2e"
          />
        </svg>
        <div className="builder-tutor" style={{ left: `${tutorX}%` }}>
          <TutorAvatar speaking={speaking} pointing />
        </div>
        <span
          className="builder-coaching"
          style={{
            left: tutorX > 35 ? '5%' : `calc(${tutorX}% + 90px)`,
            right: tutorX > 35 ? '45%' : '12px',
          }}
        >
          {correct
            ? 'Your model matches the story.'
            : 'You build it. We’ll check it together.'}
        </span>
      </div>
      <form
        className="board-entry"
        onSubmit={(e) => {
          e.preventDefault();
          onCheck();
        }}
      >
        <label htmlFor="equation-draft">Your equation proposal</label>
        <div>
          <input
            id="equation-draft"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type or say your equation"
            autoComplete="off"
            maxLength={240}
            disabled={disabled}
          />
          <button className="primary" disabled={disabled || !value.trim()}>
            Check my model <Icon name="check" />
          </button>
        </div>
        <p
          className={`board-feedback ${correct ? 'correct' : ''}`}
          role="status"
        >
          {correct
            ? 'Model checked. Explore it when ready.'
            : feedback || 'Build an idea, then check how it fits the story.'}
        </p>
      </form>
    </section>
  );
}
